import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { ImageCropData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import EditableH1TitleComponent from 'webapp/components/editable-h1-title';
import FileLogoWithUploadComponent from 'webapp/components/file/logo-with-upload';
import ReportsMasterDateRangeSwitchComponent from 'webapp/components/reports/master-date-range-switch';

import type Report from 'webapp/models-custom/report';

import type { TrackedEvent } from 'webapp/services/analytics';

export interface Args {
	model: Report;
	hideBranding: boolean;
	canEdit?: boolean;
}

export default class ReportsPagelessBottomInfoComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;

	get trackingObj(): TrackedEvent {
		return {
			event: TrackingEvent.REPORT_EDITED,
			data: { origin: 'editor' },
		};
	}

	get isSampleDataWarningShown(): boolean {
		const report = this.args.model,
			isTemplate = !!report.isTemplate,
			isAnyFragmentEligibleForWarningToBeShown = report.fragments.some(
				(fragment) => !!fragment.blockId,
			);

		return isTemplate && isAnyFragmentEligibleForWarningToBeShown;
	}

	get isMasterDateRangeSwitchShown(): boolean {
		return (
			!this.navigationService.isLoadedFromSnapshooter &&
			!!this.args.canEdit &&
			!!this.args.model.slides.some(
				(slide) => slide.hasFragmentsWithMetricData || slide.isTypeBoard,
			)
		);
	}

	get firstSlide() {
		return this.args.model.slides[0];
	}

	@action
	changeLogo(url: string, originalUrl: string, crop: ImageCropData | null): void {
		const firstSlide = this.firstSlide;
		if (!firstSlide) return;

		firstSlide.logo = url;
		firstSlide.logoOriginal = originalUrl;
		firstSlide.logoCrop = crop;
		firstSlide
			.save()
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<div class="bottomInfo {{if @canEdit 'canEdit'}}">
			<div class="bottomInfo-left">
				<FileLogoWithUploadComponent
					@image={{this.firstSlide.shownLogo}}
					@originalImage={{this.firstSlide.logoOriginal}}
					@imageCrop={{this.firstSlide.logoCrop}}
					@canEdit={{@canEdit}}
					@onChange={{this.changeLogo}}
				/>

				<EditableH1TitleComponent
					@model={{@model}}
					@canEdit={{false}}
					@trackingObj={{this.trackingObj}}
				/>
			</div>
			<div class="bottomInfo-right">
				{{#if this.isMasterDateRangeSwitchShown}}
					<ReportsMasterDateRangeSwitchComponent
						@model={{@model}}
						@customLabel="Report date range"
					/>
				{{/if}}

				{{#if this.isSampleDataWarningShown}}
					<div class="warning">
						<div class="sample">Note: Showing sample data</div>
					</div>
				{{/if}}

				{{#unless @hideBranding}}
					<a href="https://databox.com" target="_blank"><span class="powered">Powered by</span></a>
				{{/unless}}
			</div>
		</div>
	</template>
}
